import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Table from "../../../../components/table";
import { getUnreviewedReadings } from "../../../../services/patient/details";
import { exportPatientReadings } from "../../../../services/patient/documents";
import { updateReadingStatus } from "../../../../services/admin-case-manager/readings";
import { buildReportFilename, DOCTYPES } from "../../../../utils/fileNaming";
import { toast } from "react-hot-toast";
import styles from "../DailiesDetailsLayout.module.css";
import ChartsPanel from "../../../../components/patient-detail/details-tab/ChartsPanel";
import { bloodPressureColumns } from "../../../../components/patient-detail/manual-reading/blood-pressure/columns";
import { bloodGlucoseColumns } from "../../../../components/patient-detail/manual-reading/glucose/columns";
import { pulseOXColumns } from "../../../../components/patient-detail/manual-reading/pulse-ox/columns";
import { temperatureColumns } from "../../../../components/patient-detail/manual-reading/temperature/columns";
import { weightColumns } from "../../../../components/patient-detail/manual-reading/weight/columns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Download as DownloadIcon } from "lucide-react";
import { ReactComponent as ShareSvg } from "../../../../assets/icons/chart/chart-share.svg";
import { CiFileOn, CiImageOn } from "react-icons/ci";
import { Dropdown, DropdownItem } from "../../../../components/ui/dropdown";
import { getUserIdentity } from "../../../../utils/authorization";
import Button from "../../../../components/button-v1";

const DownloadReadingDialogue = React.lazy(() =>
  import("../../../../components/demo-download-dialogue")
);

const readingTypes = [
  { value: "blood_pressure", label: "Blood Pressure" },
  { value: "weight",         label: "Weight" },
  { value: "temperature",    label: "Temperature" },
  { value: "blood_glucose",  label: "Blood Glucose" },
  { value: "pulse_ox",       label: "Pulse Oximetry" },
];

const shareOptions = [
  { value: "pdf", label: "PDF", icon: <CiFileOn size={16} /> },
  { value: "png", label: "PNG", icon: <CiImageOn size={16} /> },
];

const DailiesReadingsPanel = ({ patientId, onReadingsReviewed, onUnsavedChangesChange }) => {
  const queryClient = useQueryClient();
  const [activeTab,    setActiveTab]    = useState("unreviewed");
  const [readingType,  setReadingType]  = useState("blood_pressure");
  const [timeFrame,    setTimeFrame]    = useState("1week");
  const [shareOption,  setShareOption]  = useState(null);
  const [dateRange,    setDateRange]    = useState({ startDate: "", endDate: "" });
  const [isOpen,       setIsOpen]       = useState(false);
  const [statusChanges, setStatusChanges] = useState({});
  const [isSaving,     setIsSaving]     = useState(false);

  // ChartsPanel state
  const [chartsIsOpen, setChartsIsOpen] = useState(true);
  const [patientType,  setPatientType]  = useState("RPM");
  const [chatBotType,  setChatBotType]  = useState("RPM");

  const userIdentity = getUserIdentity();
  const isPatient    = userIdentity?.isPatient || false;

  // ── Status toggle: Not reviewed → Reviewed → Escalated → Not reviewed ───────
  const toggleStatus = (readingId) => {
    setStatusChanges((prev) => {
      const current = prev[readingId] || "not_reviewed";

      if (current === "not_reviewed") {
        return { ...prev, [readingId]: "reviewed" };
      }
      if (current === "reviewed") {
        return { ...prev, [readingId]: "escalated" };
      }
      // Cycling back to not_reviewed — REMOVE the key entirely
      // so hasLocalChange becomes false and the blue border disappears
      const next = { ...prev };
      delete next[readingId];
      return next;
    });
  };

  const getCurrentStatus = (reading) => {
    const local = statusChanges[reading.id];
    if (local) return local;
    if (reading.escalated || reading.esclated) return "escalated";
    if (reading.reviewed) return "reviewed";
    return "not_reviewed";
  };

  const hasUnsavedChanges = Object.keys(statusChanges).length > 0;

  // Notify parent when unsaved changes status changes
  useEffect(() => {
    if (onUnsavedChangesChange) {
      onUnsavedChangesChange(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!hasUnsavedChanges || isSaving) return;
    setIsSaving(true);
    try {
      const reviewedIds  = [];
      const escalatedIds = [];
      Object.entries(statusChanges).forEach(([id, status]) => {
        if (status === "reviewed")  reviewedIds.push(id);
        if (status === "escalated") escalatedIds.push(id);
      });

      if (reviewedIds.length === 0 && escalatedIds.length === 0) {
        toast.error("No changes to save");
        setIsSaving(false);
        return;
      }

      const promises = [];
      if (reviewedIds.length > 0) {
        promises.push(updateReadingStatus(patientId, {
          readings: reviewedIds, reviewed: true, escalated: false, note: "", follow_up_seconds: 0,
        }));
      }
      if (escalatedIds.length > 0) {
        promises.push(updateReadingStatus(patientId, {
          readings: escalatedIds, reviewed: false, escalated: true, note: "", follow_up_seconds: 0,
        }));
      }

      const results = await Promise.all(promises);

      // Collect successful, skipped, and failed reading IDs
      const successfulIds = [];
      const errors = [];
      const skipped = [];
      const apiErrors = [];

      results.forEach((result) => {
        if (!result.success) {
          apiErrors.push(result.error || "Unknown error");
          return;
        }

        const data = result.data;
        if (data?.updated) {
          successfulIds.push(...data.updated);
        }
        if (data?.errors) {
          errors.push(...data.errors);
        }
        if (data?.skipped) {
          skipped.push(...data.skipped);
        }
      });

      // Remove successful readings from local state (keep failures/skipped for retry)
      if (successfulIds.length > 0) {
        setStatusChanges((prev) => {
          const next = { ...prev };
          successfulIds.forEach((id) => delete next[String(id)]);
          return next;
        });
      }

      // Combine errors and skipped for messaging
      const failures = [...errors, ...skipped];
      const failureCount = failures.length;

      // ── FIX: await refetch so we get fresh data before auto-switching type ──
      await queryClient.refetchQueries(["patient-readings", patientId]);

      // After refetch, check if current reading type still has unreviewed data
      const freshReadings = queryClient.getQueryData(["patient-readings", patientId]);

      if (freshReadings && Array.isArray(freshReadings)) {
        const stillUnreviewed = freshReadings.filter((r) => !r.reviewed);
        const currentTypeHasData = stillUnreviewed.some((r) => r.reading_type === readingType);

        if (!currentTypeHasData) {
          // Auto-switch to next type that still has unreviewed readings
          const availableTypes = [...new Set(stillUnreviewed.map((r) => r.reading_type))];
          if (availableTypes.length > 0) {
            setReadingType(availableTypes[0]);
          }
          // else: all reviewed — stay on current type, empty state message will show
        }
      }

      if (onReadingsReviewed) {
        onReadingsReviewed();
      }

      // Show appropriate messages
      if (apiErrors.length > 0) {
        toast.error(`Error: ${apiErrors.join(", ")}`);
      } else if (failureCount === 0 && successfulIds.length > 0) {
        toast.success(`${successfulIds.length} reading(s) updated successfully`);
      } else if (failureCount > 0 && successfulIds.length === 0) {
        const failureMessages = failures.map((e) => `• Reading ${e.id}: ${e.reason}`).join(" | ");
        toast.error(`${failureCount} update(s) failed: ${failureMessages}`, { duration: 6000 });
      } else if (failureCount > 0 && successfulIds.length > 0) {
        const failureMessages = failures.map((e) => `• Reading ${e.id}: ${e.reason}`).join(" | ");
        toast(`${successfulIds.length} updated, ${failureCount} failed: ${failureMessages}`, { icon: "⚠️", duration: 6000 });
      }
    } catch (err) {
      console.error("Error saving status:", err);
      toast.error("Failed to save status changes");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Data ─────────────────────────────────────────────────────────────────────
  const { data: readings, isLoading: readingsLoading, error: readingsError } = useQuery(
    ["patient-readings", patientId],
    () => getUnreviewedReadings(patientId),
    {
      enabled: !!patientId,
      staleTime: 0,
      refetchOnWindowFocus: false,
    }
  );

  // ── Columns ──────────────────────────────────────────────────────────────────
  const getBaseColumns = () => {
    switch (readingType) {
      case "blood_pressure": return bloodPressureColumns;
      case "weight":         return weightColumns;
      case "temperature":    return temperatureColumns;
      case "blood_glucose":  return bloodGlucoseColumns;
      case "pulse_ox":       return pulseOXColumns;
      default:               return bloodPressureColumns;
    }
  };

  const getColumnsWithStatus = () => {
    const baseColumns = getBaseColumns().map((col) => ({ ...col }));

    return [
      ...baseColumns,
      {
        key: "change_status",
        label: "Change Status",
        alignLeft: true,
        sorting: false,
        render: (reading) => {
          const currentStatus  = getCurrentStatus(reading);
          const hasLocalChange = statusChanges[reading.id] !== undefined;

          let label        = "Not reviewed";
          let statusColor  = "#6b7280";
          let statusBg     = "#ffffff";
          let statusBorder = "2px solid #d1d5db";

          if (currentStatus === "escalated") {
            label        = "Escalated";
            statusColor  = "#dc2626";
            statusBg     = "#fee2e2";
            statusBorder = "2px solid #dc2626";
          } else if (currentStatus === "reviewed") {
            label        = "Reviewed";
            statusColor  = "#059669";
            statusBg     = "#d1fae5";
            statusBorder = "2px solid #6ee7b7";
          }

          const hasUnsaved = !!statusChanges[reading.id];

          return (
            <span
              onClick={() => toggleStatus(reading.id)}
              title="Click to change status"
              style={{ border: statusBorder, backgroundColor: statusBg, color: statusColor, cursor: "pointer" }}
              className={`${styles.readingStatusBtn} ${
                currentStatus === "escalated"
                  ? styles.escalated
                  : currentStatus === "reviewed"
                  ? styles.reviewed
                  : styles.notReviewed
              } ${hasUnsaved ? ` ${styles.hasUnsavedChanges}` : ""}`}
            >
              {label}
            </span>
          );
        },
      },
    ];
  };

  // Yellow row highlight for out-of-range
  const getRowColor = (reading) =>
    reading.out_of_range ? "#FFF3B7" : "transparent";

  // SCRUM-545: the shared Table component's default hover state paints each
  // cell a near-white gray (Table.module.css `tr:hover td`), which overrides
  // this row's yellow background since that background lives on the <tr>
  // while hover repaints the <td>s on top of it. Scope a darker-yellow hover
  // to just these out-of-range rows instead of touching the shared stylesheet
  // (which every other table - including Billing - also uses).
  const getRowClassName = (reading) =>
    reading.out_of_range ? styles.outOfRangeRowHover : "";

  // ── Filtered / sorted data ───────────────────────────────────────────────────
  const filteredReadings = useMemo(() => {
    if (!readings || !Array.isArray(readings)) return [];
    if (activeTab === "unreviewed") return readings.filter((r) => !r.reviewed);
    return readings;
  }, [readings, activeTab]);

  const typedReadings = useMemo(() => {
    return filteredReadings
      .filter((r) => r.reading_type === readingType)
      // SCRUM-989: newest first by the date shown (recorded_at, when the
      // reading was taken) - created_at is when it was entered.
      .sort((a, b) => {
        const dA = new Date(a.recorded_at || a.created_at);
        const dB = new Date(b.recorded_at || b.created_at);
        return dB - dA;
      });
  }, [filteredReadings, readingType]);

  // ── Reset all local state when patient changes ──────────────────────────────
  // Component does not remount on patient navigation (same instance, new prop).
  // Must manually reset reading type, status changes, and auto-select lock
  // so each patient starts fresh.
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  useEffect(() => {
    setReadingType("blood_pressure"); // reset dropdown to default
    setStatusChanges({});             // clear any unsaved status marks
    setHasAutoSelected(false);        // allow auto-select to run for new patient
    setActiveTab("unreviewed");       // reset tab
  }, [patientId]);

  // ── Auto-select on initial data load per patient ──────────────────────────────
  // Runs once per patient load. If "blood_pressure" has no unreviewed data,
  // switches to the first type that does.
  // Does NOT run again until next patient — user dropdown never overridden.
  useEffect(() => {
    if (hasAutoSelected) return;                          // only run once per patient
    if (!readings || !Array.isArray(readings)) return;   // wait for data

    const unreviewed = readings.filter((r) => !r.reviewed);
    const currentTypeHasData = unreviewed.some((r) => r.reading_type === readingType);

    if (!currentTypeHasData) {
      const availableTypes = [...new Set(unreviewed.map((r) => r.reading_type))];
      if (availableTypes.length > 0) {
        setReadingType(availableTypes[0]);
      }
    }

    setHasAutoSelected(true); // lock — won't run again until patientId changes
  }, [readings, hasAutoSelected]);

  // ── Export ───────────────────────────────────────────────────────────────────
  useQuery({
    queryKey: ["export-download-readings", shareOption],
    queryFn: () => exportPatientReadings(patientId, shareOption, readingType, timeFrame, dateRange, activeTab === "unreviewed"),
    enabled: !!shareOption,
    refetchOnWindowFocus: false,
    cacheTime: 0,
    staleTime: 0,
    retry: false,
    onSettled: () => setShareOption(null),
    onSuccess: (data) => {
      const url  = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href  = url;
      link.setAttribute("download", buildReportFilename({
        doctype: DOCTYPES.UNREVIEWED_READINGS,
        program: "RPM",
        ext: shareOption,
      }));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Readings exported successfully");
    },
    onError: () => toast.error("Could not download readings"),
  });

  const currentReadingLabel = readingTypes.find((t) => t.value === readingType)?.label || "Readings";

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Tabs — outside white card, matches Figma chrome */}
      <div className={styles.readingsTabsRow}>
        <button
          type="button"
          className={`${styles.readingsTabBtn} ${activeTab === "unreviewed" ? styles.active : ""}`}
          onClick={() => setActiveTab("unreviewed")}
        >
          Unreviewed
        </button>
        <button
          type="button"
          className={`${styles.readingsTabBtn} ${activeTab === "all-data" ? styles.active : ""}`}
          onClick={() => setActiveTab("all-data")}
        >
          All Data
        </button>
      </div>

      {/* White card */}
      <div className={styles.readingsCard} style={{ flex: 1 }}>

        {/* ══ UNREVIEWED TAB ══ */}
        {activeTab === "unreviewed" && (
          <div className={styles.readingsTableContainer}>

            {/* Row 2: reading type dropdown — right-aligned, above table */}
            <div className={styles.readingsSubHeaderRow}>
              <div className={styles.readingsTypeSelector}>
                <Select value={readingType} onChange={(v) => setReadingType(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type">
                      {readingTypes.find((t) => t.value === readingType)?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {readingTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 1: heading + legend + export — matches Figma */}
            <div className={styles.readingsHeaderRow}>
              <div>
                <h3 className={styles.readingTypeTitle}>{currentReadingLabel}</h3>
                <p className={styles.readingTypeSubtitle}>All Readings</p>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {/* Out-of-range legend */}
                <div className={styles.outOfRangeLegend}>
                  <span className={styles.outOfRangeSwatch} />
                  <span>= Out of range</span>
                </div>

                {/* Export buttons */}
                {!isPatient && (
                  <div className={styles.exportIconsGroup}>
                    <button
                      type="button"
                      className={styles.iconCircleBtn}
                      onClick={() => setIsOpen(true)}
                      disabled={!readings || readings.length === 0}
                      aria-label="Share"
                      title="Share"
                    >
                      <ShareSvg className={styles.iconCircleSvg} />
                    </button>
                    <Dropdown
                      value={shareOption}
                      onChange={(v) => setShareOption(v)}
                      trigger={
                        <button
                          type="button"
                          className={styles.iconCircleBtn}
                          disabled={!readings || readings.length === 0}
                          aria-label="Download"
                          title="Download"
                        >
                          <DownloadIcon size={16} />
                        </button>
                      }
                    >
                      {shareOptions.map((opt) => (
                        <DropdownItem key={opt.value} value={opt.value} className={styles.dropdownItem}>
                          {opt.icon}{opt.label}
                        </DropdownItem>
                      ))}
                    </Dropdown>
                  </div>
                )}
              </div>
            </div>

            {/* ── FIX: Table area — card always visible, empty state inside ── */}
            {readingsLoading ? (
              <div className={styles.emptyState}>Loading readings...</div>
            ) : readingsError ? (
              <div className={styles.emptyState}>Error loading readings</div>
            ) : (
              <>
                {typedReadings.length > 0 ? (
                  <div className={styles.readingsTableScroll}>
                    <Table
                      columns={getColumnsWithStatus()}
                      data={typedReadings}
                      height={500}
                      rowColor={getRowColor}
                      getRowClassName={getRowClassName}
                    />
                  </div>
                ) : (
                  // ── FIX: Box stays visible, shows message instead of collapsing
                  <div className={styles.emptyState}>
                    No unreviewed {currentReadingLabel} readings available.
                  </div>
                )}

                {/* Save button — always at bottom when unsaved changes exist */}
                {hasUnsavedChanges && (
                  <div className={styles.saveSection}>
                    <Button onClick={handleSave} loading={isSaving}>
                      Save Changes ({Object.keys(statusChanges).length})
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ══ ALL DATA TAB ══ */}
        {activeTab === "all-data" && (
          <div style={{ flex: 1, overflow: "hidden", margin: "10px" }}>
            <ChartsPanel
              patientId={patientId}
              isOpen={chartsIsOpen}
              onClose={() => setChartsIsOpen(false)}
              handlePatientType={setPatientType}
              setChatBotType={setChatBotType}
              isCcmChatStarted={false}
              patientType={patientType}
              chatBotType={chatBotType}
            />
          </div>
        )}

        {/* Share modal */}
        {!isPatient && (
          <React.Suspense fallback={<div>Loading...</div>}>
            <DownloadReadingDialogue
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              readingType={readingType}
              timeFrame={timeFrame}
              dateRange={dateRange}
              unreviewed={activeTab === "unreviewed"}
            />
          </React.Suspense>
        )}
      </div>
    </div>
  );
};

export default DailiesReadingsPanel;